import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Activity, Zap, CheckCircle, XCircle } from 'lucide-react';
import socketService from '../services/socketService';

const Container = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Subtitle = styled.p`
  color: #6b7280;
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 32px;
  background: #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  margin-top: 1rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
`;

const StatusText = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
  color: #374151;
  font-size: 0.9rem;
  font-family: 'Consolas', 'Monaco', monospace;
`;

const ConnectionStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${props => props.connected ? '#d1fae5' : '#fee2e2'};
  color: ${props => props.connected ? '#065f46' : '#991b1b'};
  border-radius: 8px;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const LogContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  background: #1f2937;
  color: #f3f4f6;
  padding: 1rem;
  border-radius: 8px;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.85rem;
  margin-top: 1rem;
`;

const LogEntry = styled.div`
  margin-bottom: 0.5rem;
  padding: 0.25rem 0;
  border-bottom: 1px solid #374151;
  
  &:last-child {
    border-bottom: none;
  }
  
  span {
    color: #60a5fa;
    font-weight: 600;
  }
`;

const TestWebSockets = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [logs, setLogs] = useState([]);

  const addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message }]);
  };

  useEffect(() => {
    // Socket.IO auto-connects
    addLog('Socket.IO service ready');
    
    // Check connection status
    const checkConnection = setInterval(() => {
      const connected = socketService.isReady();
      setIsConnected(connected);
    }, 1000);

    // Subscribe to progress updates
    const handleProgress = (data) => {
      console.log('📊 Progress update received:', data);
      const payload = data.payload || data.data || data;
      setProgress(payload.progress || 0);
      setStatus(payload.status || payload.message || '');
      addLog(`Progress: ${payload.progress}% - ${payload.status || payload.message || ''}`);
    };

    const handleComplete = (data) => {
      console.log('✅ Progress complete:', data);
      addLog('Progress completed!');
      setIsRunning(false);
    };

    socketService.subscribe('progress_update', handleProgress);
    socketService.subscribe('progress_complete', handleComplete);

    addLog('Socket.IO event listeners registered');

    return () => {
      socketService.unsubscribe('progress_update', handleProgress);
      socketService.unsubscribe('progress_complete', handleComplete);
      clearInterval(checkConnection);
    };
  }, []);

  const startTest = async () => {
    setIsRunning(true);
    setProgress(0);
    setStatus('Starting test...');
    addLog('🚀 Starting test progress simulation...');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000/api'}/test/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      addLog(`✅ API responded: ${result.message}`);
    } catch (error) {
      console.error('Error starting test:', error);
      addLog(`❌ Error: ${error.message}`);
      setIsRunning(false);
      setStatus(`Error: ${error.message}`);
    }
  };

  return (
    <Container>
      <Title>
        <Activity size={32} />
        WebSocket Progress Test
      </Title>
      <Subtitle>
        Test WebSocket connection and progress bar updates on AWS deployment
      </Subtitle>

      <Section>
        <SectionTitle>Connection Status</SectionTitle>
        <ConnectionStatus connected={isConnected}>
          {isConnected ? (
            <>
              <CheckCircle size={20} />
              Connected to WebSocket
            </>
          ) : (
            <>
              <XCircle size={20} />
              Not Connected
            </>
          )}
        </ConnectionStatus>
        
        <Button onClick={startTest} disabled={isRunning || !isConnected}>
          <Zap size={20} />
          {isRunning ? 'Test Running...' : 'Start 5-Second Test'}
        </Button>

        {(progress > 0 || status) && (
          <>
            <ProgressBar>
              <ProgressFill style={{ width: `${progress}%` }}>
                {progress}%
              </ProgressFill>
            </ProgressBar>
            {status && <StatusText>{status}</StatusText>}
          </>
        )}
      </Section>

      <Section>
        <SectionTitle>Event Log</SectionTitle>
        <LogContainer>
          {logs.length === 0 ? (
            <div style={{ color: '#9ca3af' }}>No events yet...</div>
          ) : (
            logs.map((log, idx) => (
              <LogEntry key={idx}>
                <span>[{log.timestamp}]</span> {log.message}
              </LogEntry>
            ))
          )}
        </LogContainer>
      </Section>
    </Container>
  );
};

export default TestWebSockets;

